/* socket.io-client weighs ~25 KB gzip and isn't needed for first paint — the
 * connection is established only after auth resolves and the workspace context
 * loads. We dynamic-import the lib on the first connectSocket() call so it
 * lands in its own chunk and stays out of the shell entry.
 *
 * Race-condition note: React runs child effects before parent effects. Hooks
 * like useInboxEvents (deep in the tree) fire before SocketConnector.useEffect
 * (an ancestor) calls connectSocket. With a synchronous io() call this didn't
 * matter — the socket object existed the moment connectSocket was called. With
 * the async dynamic import the socket isn't ready yet when child effects run.
 *
 * We solve this with an onSocketReady() subscription: hooks register a callback
 * that fires immediately if the socket already exists, or is queued and drained
 * the moment the dynamic import resolves and the socket object is created. The
 * socket doesn't need to be *connected* for listeners to be registered — socket.io
 * queues them internally until the handshake completes. */

import type { Socket } from 'socket.io-client';

const baseURL = import.meta.env.VITE_API_BASE_URL || '';

let socket: Socket | null = null;
let connectPromise: Promise<Socket> | null = null;

type SocketReadyCallback = (s: Socket) => void;
const pendingCallbacks: SocketReadyCallback[] = [];

/** Register a callback to receive the socket once it is created.
 * If the socket already exists the callback fires synchronously and immediately.
 * Otherwise it is queued and fires when connectSocket() finishes its async import.
 * Returns an unsubscribe function — call it from effect cleanup to avoid leaks. */
export function onSocketReady(cb: SocketReadyCallback): () => void {
  if (socket) {
    cb(socket);
    return () => {};
  }
  pendingCallbacks.push(cb);
  return () => {
    const idx = pendingCallbacks.indexOf(cb);
    if (idx !== -1) pendingCallbacks.splice(idx, 1);
  };
}

export function connectSocket(appId: string, userId: string): Promise<Socket> {
  if (socket?.connected) return Promise.resolve(socket);
  if (connectPromise) return connectPromise;

  connectPromise = import('socket.io-client').then(({ io }) => {
    const next = io(baseURL, {
      path: '/coreleads/socket.io/',
      extraHeaders: { 'gamix-app-id': appId },
      auth: { userId },
      withCredentials: true,
      reconnectionAttempts: 5,
    });
    socket = next;
    // Drain any hooks that registered before connectSocket was called.
    for (const cb of pendingCallbacks) cb(next);
    pendingCallbacks.length = 0;
    return next;
  });

  return connectPromise;
}

export function getSocket(): Socket | null {
  return socket;
}

export function disconnectSocket(): void {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
  connectPromise = null;
  pendingCallbacks.length = 0;
}
